import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  SidePanelLoader,
  ButtonPrimary,
  ButtonPseudo,
  InputText,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetSicCodeQuery,
  useUpdateSicCodeRiskScoreMutation,
} from '../store/services';
import ROUTES from '../../../routes';

import cn from './styles.module.css';

const SicCodePanel = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [riskScoreValue, setRiskScoreValue] = useState('');
  const [riskCategoryValue, setRiskCategoryValue] = useState('');
  const [globalError, setGlobalError] = useState('');

  const navigate = useNavigate();

  const { id } = useParams();

  const {
    corporateClientsRisk: {
      sicCodes: { sicCode },
    },
    amlDictionaries: { riskScoreRanges },
  } = useAppSelector((state) => state);

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useForm();

  const [getSicCode, { isLoading, isFetching }] = useLazyGetSicCodeQuery();
  const [updateRiskScore, { isLoading: requestInProgress }] =
    useUpdateSicCodeRiskScoreMutation();

  const onSubmit = async () => {
    setGlobalError('');

    let requestAvailable = true;

    if (Number.isNaN(Number(riskScoreValue))) {
      requestAvailable = false;

      if (Number.isNaN(Number(riskScoreValue))) {
        setError('riskScore', {
          type: 'custom',
          message: 'Risk Score must be an integer number',
        });
      }
    }

    if (requestAvailable) {
      const response = (await updateRiskScore({
        id: id as string,
        body: {
          riskScore: riskScoreValue ? Number(riskScoreValue) : 0,
        },
      })) as { error?: ResponseError };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      }
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setGlobalError('');
    clearErrors(['riskScore']);

    setTimeout(() => {
      navigate(ROUTES.CORPORATE_CLIENTS_RISK_SIC_CODES);
    }, 100);
  };

  useEffect(() => {
    if (sicCode) {
      setRiskScoreValue(sicCode.riskScore.toString());

      const riskCategory = riskScoreRanges?.find(
        (riskScore) =>
          riskScore?.from <= sicCode.riskScore &&
          riskScore.to >= sicCode.riskScore,
      );

      setRiskCategoryValue(
        `${riskCategory?.name?.charAt(0)}${riskCategory?.name
          .slice(1)
          .toLowerCase()} risk`,
      );
    }
  }, [sicCode]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (id) {
      getSicCode(id);
    }
  }, [id]);

  let content = <div />;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (!isLoading && !isFetching && sicCode) {
    content = (
      <>
        <div className={cn.formGroup}>
          <InputText
            name="code"
            value={sicCode?.code}
            title="Code"
            placeholder=""
            disabled
          />
        </div>
        <div className={cn.formGroup}>
          <InputText
            name="description"
            title="Description"
            placeholder=""
            value={sicCode?.description}
            disabled
          />
        </div>
        <div className={cn.formGroup}>
          <InputText
            name="riskScore"
            title="Risk Score"
            placeholder=""
            value={riskScoreValue}
            onChange={(e) => {
              setRiskScoreValue(e.target.value);

              if (errors?.riskScore?.message) {
                clearErrors(['riskScore']);
              }

              const riskCategory = riskScoreRanges?.find(
                (riskScore) =>
                  riskScore?.from <= Number(e.target.value) &&
                  riskScore.to >= Number(e.target.value),
              );

              setRiskCategoryValue(
                `${riskCategory?.name?.charAt(0)}${riskCategory?.name
                  .slice(1)
                  .toLowerCase()} risk`,
              );
            }}
            error={errors?.riskScore?.message as string}
          />
        </div>
        <div className={cn.formGroup}>
          <InputText
            name="riskCategory"
            title="Risk Category"
            placeholder=""
            value={riskCategoryValue}
            disabled
          />
        </div>
      </>
    );
  }

  return (
    <SidePanel
      title="SIC Code Details"
      type={SidePanelType.AML}
      isOpen={panelIsOpen}
      onClose={handleClose}
      sidepanelExpandedKey="sic-codes"
      errorSidepanel={globalError}
      footer={
        <>
          <ButtonPrimary
            text={requestInProgress ? 'Loading...' : 'Save'}
            disabled={
              Boolean(Number(riskScoreValue) === sicCode?.riskScore) ||
              requestInProgress
            }
            onClick={onSubmit}
            bold
          />
          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </>
      }
      closeOnClickOutside
    >
      {content}
    </SidePanel>
  );
};

export default SicCodePanel;
